import { useState, useEffect } from "react";

/**
 * Detects whether the site is running in "app mode" — either:
 * 1. Installed as a PWA (display-mode: standalone)
 * 2. Opened with ?app=1 query parameter
 * 3. Previously opened in app mode (persisted in sessionStorage)
 */
export function useAppMode(): boolean {
  const [isApp, setIsApp] = useState(() => {
    if (typeof window === "undefined") return false;
    // Check sessionStorage first (persists across navigations within session)
    if (sessionStorage.getItem("fw-app-mode") === "1") return true;
    // Check query param
    const params = new URLSearchParams(window.location.search);
    if (params.get("app") === "1") return true;
    // Check display-mode: standalone (installed PWA)
    if (window.matchMedia("(display-mode: standalone)").matches) return true;
    // iOS Safari standalone mode
    if ((navigator as any).standalone === true) return true;
    return false;
  });

  useEffect(() => {
    if (isApp) {
      sessionStorage.setItem("fw-app-mode", "1");
    }
  }, [isApp]);

  // Listen for display-mode changes (e.g., user installs PWA while browsing)
  useEffect(() => {
    const mq = window.matchMedia("(display-mode: standalone)");
    const handler = (e: MediaQueryListEvent) => {
      if (e.matches) setIsApp(true);
    };
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  return isApp;
}
